import { makeAutoObservable } from 'mobx';
import api from '../config/api';
import {
    Deployment,
    DeploymentTemplate,
    DeploymentStats,
    DeploymentRequest,
    DeploymentResponse,
    DeploymentLog,
    DeploymentTemplateCreateRequest,
    DeploymentTemplateUpdateRequest,
    DeploymentListResponse,
    DeploymentStatus
} from '../types/deployment';

export class DeploymentStore {
    deployments: Deployment[] = [];
    templates: DeploymentTemplate[] = [];
    stats: DeploymentStats | null = null;
    logs: DeploymentLog[] = [];
    loading = false;
    error: string | null = null;
    saving = false;
    total = 0;
    page = 1;
    limit = 20;

    constructor() {
        makeAutoObservable(this);
    }

    // Deployments
    async fetchDeployments(page = 1, limit = 20, status?: string) {
        this.loading = true;
        this.error = null;
        this.page = page;
        this.limit = limit;

        try {
            const params: any = { page, limit };
            if (status) params.status = status;

            const response = await api.get<DeploymentListResponse>('/api/deployments', { params });
            this.deployments = response.data.deployments;
            this.total = response.data.total;
        } catch (error: any) {
            this.error = error.response?.data?.error || 'Failed to fetch deployments';
            this.deployments = [];
        } finally {
            this.loading = false;
        }
    }

    async getDeployment(id: number): Promise<Deployment | null> {
        try {
            const response = await api.get<Deployment>(`/api/deployments/${id}`);
            return response.data;
        } catch (error: any) {
            this.error = error.response?.data?.error || 'Failed to fetch deployment';
            return null;
        }
    }

    async createDeployment(request: DeploymentRequest): Promise<DeploymentResponse | null> {
        this.saving = true;
        this.error = null;

        try {
            const response = await api.post<DeploymentResponse>('/api/deployments', request);
            await this.fetchDeployments(); // Refresh list
            return response.data;
        } catch (error: any) {
            this.error = error.response?.data?.error || 'Failed to create deployment';
            return null;
        } finally {
            this.saving = false;
        }
    }

    async stopDeployment(id: number): Promise<boolean> {
        this.saving = true;
        this.error = null;

        try {
            await api.delete(`/api/deployments/${id}`);
            await this.fetchDeployments(); // Refresh list
            return true;
        } catch (error: any) {
            this.error = error.response?.data?.error || 'Failed to stop deployment';
            return false;
        } finally {
            this.saving = false;
        }
    }

    async restartDeployment(id: number): Promise<DeploymentResponse | null> {
        this.saving = true;
        this.error = null;

        try {
            const response = await api.post<DeploymentResponse>(`/api/deployments/${id}/restart`);
            await this.fetchDeployments(); // Refresh list
            return response.data;
        } catch (error: any) {
            this.error = error.response?.data?.error || 'Failed to restart deployment';
            return null;
        } finally {
            this.saving = false;
        }
    }

    async fetchDeploymentLogs(id: number) {
        this.loading = true;
        this.error = null;

        try {
            const response = await api.get<DeploymentLog[]>(`/api/deployments/${id}/logs`);
            this.logs = response.data;
        } catch (error: any) {
            this.error = error.response?.data?.error || 'Failed to fetch deployment logs';
            this.logs = [];
        } finally {
            this.loading = false;
        }
    }

    async fetchDeploymentStats() {
        try {
            const response = await api.get<DeploymentStats>('/api/deployments/stats');
            this.stats = response.data;
        } catch (error: any) {
            this.error = error.response?.data?.error || 'Failed to fetch deployment stats';
        }
    }

    async checkDeploymentHealth(id: number): Promise<any> {
        try {
            const response = await api.get(`/api/deployments/${id}/health`);
            return response.data;
        } catch (error: any) {
            this.error = error.response?.data?.error || 'Failed to check deployment health';
            return null;
        }
    }

    // Templates
    async fetchTemplates(active?: boolean, tag?: string) {
        this.loading = true;
        this.error = null;

        try {
            const params: any = {};
            if (active !== undefined) params.active = active;
            if (tag) params.tag = tag;

            const response = await api.get<DeploymentTemplate[]>('/api/deployment-templates', { params });
            this.templates = response.data;
        } catch (error: any) {
            this.error = error.response?.data?.error || 'Failed to fetch templates';
            this.templates = [];
        } finally {
            this.loading = false;
        }
    }

    async getTemplate(id: number): Promise<DeploymentTemplate | null> {
        try {
            const response = await api.get<DeploymentTemplate>(`/api/deployment-templates/${id}`);
            return response.data;
        } catch (error: any) {
            this.error = error.response?.data?.error || 'Failed to fetch template';
            return null;
        }
    }

    async createTemplate(templateData: DeploymentTemplateCreateRequest): Promise<boolean> {
        this.saving = true;
        this.error = null;

        try {
            const response = await api.post<DeploymentTemplate>('/api/deployment-templates', templateData);
            this.templates.push(response.data);
            return true;
        } catch (error: any) {
            this.error = error.response?.data?.error || 'Failed to create template';
            return false;
        } finally {
            this.saving = false;
        }
    }

    async updateTemplate(id: number, templateData: DeploymentTemplateUpdateRequest): Promise<boolean> {
        this.saving = true;
        this.error = null;

        try {
            const response = await api.put<DeploymentTemplate>(`/api/deployment-templates/${id}`, templateData);
            const index = this.templates.findIndex(t => t.id === id);
            if (index !== -1) {
                this.templates[index] = response.data;
            }
            return true;
        } catch (error: any) {
            this.error = error.response?.data?.error || 'Failed to update template';
            return false;
        } finally {
            this.saving = false;
        }
    }

    async deleteTemplate(id: number): Promise<boolean> {
        this.saving = true;
        this.error = null;

        try {
            await api.delete(`/api/deployment-templates/${id}`);
            this.templates = this.templates.filter(t => t.id !== id);
            return true;
        } catch (error: any) {
            this.error = error.response?.data?.error || 'Failed to delete template';
            return false;
        } finally {
            this.saving = false;
        }
    }

    async cloneTemplate(id: number): Promise<DeploymentTemplate | null> {
        this.saving = true;
        this.error = null;

        try {
            const response = await api.post<DeploymentTemplate>(`/api/deployment-templates/${id}/clone`);
            this.templates.push(response.data);
            return response.data;
        } catch (error: any) {
            this.error = error.response?.data?.error || 'Failed to clone template';
            return null;
        } finally {
            this.saving = false;
        }
    }

    async validateTemplate(templateData: Partial<DeploymentTemplate>): Promise<any> {
        try {
            const response = await api.post('/api/deployment-templates/validate', templateData);
            return response.data;
        } catch (error: any) {
            return { valid: false, errors: error.response?.data?.errors || {} };
        }
    }

    async previewTemplate(templateId: number, envVars: { [key: string]: string }): Promise<any> {
        try {
            const response = await api.post('/api/deployment-templates/preview', {
                template_id: templateId,
                env_vars: envVars
            });
            return response.data;
        } catch (error: any) {
            this.error = error.response?.data?.error || 'Failed to preview template';
            return null;
        }
    }

    // Getters
    get activeDeployments() {
        return this.deployments.filter(d => d.status === 'completed');
    }

    get failedDeployments() {
        return this.deployments.filter(d => d.status === 'failed');
    }

    get pendingDeployments() {
        return this.deployments.filter(d =>
            ['build_image', 'deploy_db', 'insert_dump_db', 'deploy_container', 'config_container'].includes(d.status)
        );
    }

    get activeTemplates() {
        return this.templates.filter(t => t.is_active);
    }

    getDeploymentById(id: number): Deployment | undefined {
        return this.deployments.find(d => d.id === id);
    }

    getTemplateById(id: number): DeploymentTemplate | undefined {
        return this.templates.find(t => t.id === id);
    }

    getDeploymentsByApplicationId(applicationId: number): Deployment[] {
        return this.deployments.filter(d => d.application_id === applicationId);
    }

    getDeploymentsByTemplateId(templateId: number): Deployment[] {
        return this.deployments.filter(d => d.template_id === templateId);
    }

    getStatusColor(status: DeploymentStatus): string {
        const colors: Record<DeploymentStatus, string> = {
            'build_image': 'info',
            'deploy_db': 'info',
            'insert_dump_db': 'info',
            'deploy_container': 'info',
            'config_container': 'info',
            'completed': 'success',
            'failed': 'error'
        };
        return colors[status] || 'default';
    }

    getStatusText(status: DeploymentStatus): string {
        const texts: Record<DeploymentStatus, string> = {
            'build_image': 'Сборка образа',
            'deploy_db': 'Создание БД',
            'insert_dump_db': 'Загрузка данных',
            'deploy_container': 'Развертывание контейнера',
            'config_container': 'Настройка контейнера',
            'completed': 'Завершено',
            'failed': 'Ошибка'
        };
        return texts[status] || status;
    }

    resetError() {
        this.error = null;
    }

    clearDeployments() {
        this.deployments = [];
        this.total = 0;
        this.page = 1;
    }

    clearTemplates() {
        this.templates = [];
    }

    clearLogs() {
        this.logs = [];
    }

    clearStats() {
        this.stats = null;
    }
}